/**
 * ProductManagement - Add/Edit Products with Images
 * Merchant product management dashboard
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import ProductVideoUpload from "../components/ProductVideoUpload";
import SellerPolicyModal from '../components/SellerPolicyModal';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Package,
  Plus,
  Edit2,
  Trash2,
  Image,
  Upload,
  Save,
  Loader2,
  Check,
  X,
  Eye,
  EyeOff,
  DollarSign,
  Tag,
  Layers,
  Search,
  Scale,
  Gavel,
  Clock,
  ShoppingBag,
  AlertCircle,
} from 'lucide-react';

// Product categories
const CATEGORIES = [
  'Electronics',
  'Fashion',
  'Home & Garden',
  'Food & Beverages',
  'Health & Beauty',
  'Sports & Outdoors',
  'Books & Media',
  'Toys & Games',
  'Automotive',
  'Services',
  'Other'
];

// Product conditions
const PRODUCT_CONDITIONS = [
  { value: 'NEW_PACKAGED', label: 'New (sealed packaging)' },
  { value: 'OPEN_BOX', label: 'Open box' },
  { value: 'REFURBISHED', label: 'Refurbished' },
  { value: 'USED', label: 'Used' }
];

// Sale modes
const SALE_MODES = [
  { value: 'FIXED_PRICE', label: 'Fixed Price', icon: DollarSign },
  { value: 'AUCTION', label: 'Auction Only', icon: Gavel },
  { value: 'FIXED_OR_AUCTION', label: 'Auction + Buy Now', icon: ShoppingBag }
];

export default function ProductManagement() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(!!productId || window.location.pathname.endsWith('/new'));
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(() => localStorage.getItem("liantiao_seller_policy_accepted") === "true");
  const [editingProduct, setEditingProduct] = useState(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [onSale, setOnSale] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [freeShipping, setFreeShipping] = useState(false);
  const [sku, setSku] = useState('');
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  
  // Phase 1: Condition
  const [condition, setCondition] = useState('NEW_PACKAGED');
  
  // Phase 2: Auction fields
  const [saleMode, setSaleMode] = useState('FIXED_PRICE');
  const [buyNowEnabled, setBuyNowEnabled] = useState(false);
  const [buyNowPrice, setBuyNowPrice] = useState('');
  const [auctionStartAt, setAuctionStartAt] = useState('');
  const [auctionEndAt, setAuctionEndAt] = useState('');
  const [auctionMinBid, setAuctionMinBid] = useState('');
  const [auctionMinIncrement, setAuctionMinIncrement] = useState('100');

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/merchant/products');
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProduct = useCallback(async (id) => {
    try {
      const res = await api.get(`/products/${id}`);
      const product = res.data;
      setEditingProduct(product);
      setTitle(product.title || '');
      setDescription(product.description || '');
      setPrice(product.price?.toString() || '');
      setWeightLbs(product.weight_lbs?.toString() || '');
      setCategory(product.category || '');
      setTags(product.tags?.join(', ') || '');
      setImages(product.images || []);
      setVideos(product.videos || []);
      setFreeShipping(product.free_shipping || false);
      setOnSale(product.on_sale || false);
      setSalePrice(product.sale_price?.toString() || '');
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    if (productId) {
      fetchProduct(productId);
    }
  }, [fetchProducts, fetchProduct, productId]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });
    
    if (images.length + imageFiles.length + validFiles.length > 10) {
      toast.error('Maximum 10 images per product');
      return;
    }
    
    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageFiles(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index, isExisting) => {
    if (isExisting) {
      setImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setImageFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const uploadImages = async () => {
    const uploadedUrls = [];
    
    for (const { file } of imageFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await api.post('/security/verify/upload-document?doc_type=product_image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(res.data.file_path);
      } catch (error) {
        console.error('Upload error:', error);
        throw error;
      }
    }
    
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a product title');
      return;
    }
    
    if (!weightLbs || parseFloat(weightLbs) <= 0) {
      toast.error("Weight is required for shipping calculation");
      return;
    }
    
    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setSaving(true);
    
    try {
      let allImages = [...images];
      
      // Upload new images
      if (imageFiles.length > 0) {
        setUploading(true);
        const uploadedUrls = await uploadImages();
        allImages = [...allImages, ...uploadedUrls];
        setUploading(false);
      }
      
      
      // Process variants with image uploads
      const processedVariants = await Promise.all(
        variants.filter(v => v.name).map(async (v) => {
          let variantImage = v.image;
          if (v.imageFile) {
            try {
              const formData = new FormData();
              formData.append("file", v.imageFile);
              const res = await api.post("/security/verify/upload-document?doc_type=variant_image", formData, {
                headers: { "Content-Type": "multipart/form-data" }
              });
              variantImage = res.data.file_path;
            } catch (err) {
              console.error("Failed to upload variant image:", err);
            }
          }
          return {
            name: v.name,
            price: v.price ? parseFloat(v.price) : null,
            stock: v.stock ? parseInt(v.stock) : null,
            sku: v.sku || null,
            image: variantImage
          };
        })
      );
      
      const productData = {
        title: title.trim(),
        description: description.trim() || null,
        price: priceNum,
        category: category || null,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        images: allImages,
        weight_lbs: parseFloat(weightLbs) || 0,
        // Phase 1: Condition
        condition: condition,
        // Phase 2: Auction fields
        sale_mode: saleMode,
        buy_now_enabled: saleMode === 'FIXED_OR_AUCTION',
        buy_now_price_jmd: buyNowPrice ? parseFloat(buyNowPrice) : null,
        auction_enabled: saleMode === 'AUCTION' || saleMode === 'FIXED_OR_AUCTION',
        auction_start_at: auctionStartAt ? new Date(auctionStartAt).toISOString() : null,
        auction_end_at: auctionEndAt ? new Date(auctionEndAt).toISOString() : null,
        auction_min_bid_jmd: auctionMinBid ? parseFloat(auctionMinBid) : null,
        auction_min_increment_jmd: auctionMinIncrement ? parseFloat(auctionMinIncrement) : 100,
        variants: processedVariants,
        sku: sku || null,
        stock_quantity: parseInt(stockQuantity) || 0,
        original_price: originalPrice ? parseFloat(originalPrice) : null,
        free_shipping: freeShipping,
        on_sale: onSale,
        sale_price: onSale ? parseFloat(salePrice) || 0 : null
      };
      
      if (editingProduct) {
        // Update existing product
        await api.put(`/merchant/products/${editingProduct.id}`, productData);
        toast.success('Product updated!');
      } else {
        // Create new product
        await api.post('/merchant/products', productData);
        toast.success('Product created!');
      }
      
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setTitle('');
    setDescription('');
    setPrice('');
    setOnSale(false);
    setSalePrice('');
    setWeightLbs('');
    setCategory('');
    setTags('');
    setImages([]);
    setImageFiles([]);
    setShowForm(false);
    if (productId) {
      navigate('/merchant/products');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setTitle(product.title || '');
    setDescription(product.description || '');
    setPrice(product.price?.toString() || '');
    setWeightLbs(product.weight_lbs?.toString() || '');
    setCategory(product.category || '');
    setTags(product.tags?.join(', ') || '');
    setImages(product.images || []);
    setImageFiles([]);
    setVideos(product.videos || []);
    // Load condition and auction fields
    setCondition(product.condition || 'NEW_PACKAGED');
    setSaleMode(product.sale_mode || 'FIXED_PRICE');
    setBuyNowEnabled(product.buy_now_enabled || false);
    setBuyNowPrice(product.buy_now_price_jmd?.toString() || '');
    // Format datetime for input
    if (product.auction_start_at) {
      const start = new Date(product.auction_start_at);
      setAuctionStartAt(start.toISOString().slice(0, 16));
    } else {
      setAuctionStartAt('');
    }
    if (product.auction_end_at) {
      const end = new Date(product.auction_end_at);
      setAuctionEndAt(end.toISOString().slice(0, 16));
    } else {
      setAuctionEndAt('');
    }
    setAuctionMinBid(product.auction_min_bid_jmd?.toString() || '');
    setAuctionMinIncrement(product.auction_min_increment_jmd?.toString() || '100');
    setShowForm(true);
  };

  const handleToggleActive = async (product) => {
    try {
      await api.patch(`/merchant/products/${product.id}/toggle-active`);
      toast.success(product.is_active ? 'Product hidden' : 'Product visible');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    try {
      await api.delete(`/merchant/products/${productId}`);
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete product");
    }
  };

  const filteredProducts = products.filter(p =>
    !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SellerPolicyModal open={showPolicyModal} onOpenChange={setShowPolicyModal} onAccept={() => { setPolicyAccepted(true); setShowPolicyModal(false); setShowForm(true); }} />
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => showForm ? resetForm() : navigate('/business')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#00A86B]" />
                  {showForm ? (editingProduct ? 'Edit Product' : 'New Product') : 'Product Management'}
                </h1>
                <p className="text-sm text-slate-400">
                  {showForm ? 'Fill in product details' : `${products.length} products`}
                </p>
              </div>
            </div>
            
            {!showForm && (
              <Button
                onClick={() => policyAccepted ? setShowForm(true) : setShowPolicyModal(true)}
                className="bg-[#00A86B] hover:bg-[#00A86B]/90"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {showForm ? (
          /* Product Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Product Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter product name"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="product-title-input"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your product..."
                    className="bg-slate-900 border-slate-700 mt-1 min-h-[100px]"
                    data-testid="product-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Price (JMD) *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        className="bg-slate-900 border-slate-700 pl-10"
                        data-testid="product-price-input"
                        required
                      />
                    </div>
                    
                    {/* Sale Price Toggle */}
                    <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <Label className="text-slate-300">Put on Sale</Label>
                          <p className="text-xs text-slate-500">Show discounted price to attract buyers</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOnSale(!onSale)}
                          className={`w-12 h-6 rounded-full transition-colors ${onSale ? 'bg-red-500' : 'bg-slate-600'}`}
                          data-testid="on-sale-toggle"
                        >
                          <div className={`w-5 h-5 rounded-full bg-white transition-transform ${onSale ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      {onSale && (
                        <div>
                          <Label className="text-slate-300 text-sm">Sale Price (JMD)</Label>
                          <div className="relative mt-1">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={salePrice}
                              onChange={(e) => setSalePrice(e.target.value)}
                              placeholder="0.00"
                              className="bg-slate-900 border-red-500/50 pl-10 text-red-400"
                              data-testid="sale-price-input"
                            />
                          </div>
                          {price && salePrice && parseFloat(salePrice) < parseFloat(price) && (
                            <p className="text-xs text-emerald-400 mt-2">
                              Discount: {Math.round((1 - parseFloat(salePrice) / parseFloat(price)) * 100)}% off
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Category</Label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                      data-testid="product-category-select"
                    >
                      <option value="">Select category...</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Stock & Original Price */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Stock Quantity</Label>
                    <Input
                      type="number"
                      min="0"
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      placeholder="Available units"
                      className="bg-slate-900 border-slate-700 mt-1"
                      data-testid="product-stock-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Original Price (JMD)</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={originalPrice}
                        onChange={(e) => setOriginalPrice(e.target.value)}
                        placeholder="Compare-at price"
                        className="bg-slate-900 border-slate-700 pl-10"
                        data-testid="product-original-price-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Shows as strikethrough if higher than price</p>
                  </div>
                </div>

                {/* Free Shipping Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div>
                    <Label className="text-slate-300">Free Shipping for Buyers</Label>
                    <p className="text-xs text-slate-500">Attract more buyers with free delivery</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFreeShipping(!freeShipping)}
                    className={`w-12 h-6 rounded-full transition-colors ${freeShipping ? 'bg-emerald-500' : 'bg-slate-600'}`}
                    data-testid="free-shipping-toggle"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${freeShipping ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                {freeShipping && (
                  <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-200">
                        <p className="font-semibold mb-1">Important Notice:</p>
                        <p>When you offer free shipping, customers see "Free Shipping" but Liantiao will still deduct the calculated shipping cost from your payout. This amount covers actual delivery expenses.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Variants */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Variants</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setVariants([...variants, { name: '', price: '', stock: '', sku: '', image: null, imageFile: null, imagePreview: null }])}
                      className="text-xs border-slate-600"
                      data-testid="add-variant-btn"
                    >
                      + Add Variant
                    </Button>
                  </div>
                  {variants.length > 0 && (
                    <div className="space-y-2">
                      {variants.map((v, i) => (
                        <div key={i} className="flex gap-3 items-start bg-slate-900/50 p-2 rounded-lg border border-slate-700" data-testid={"variant-row-" + i}>
                          {/* Variant Image */}
                          <div className="w-14 h-14 flex-shrink-0 bg-slate-800 rounded-lg border border-slate-600 overflow-hidden relative group">
                            {v.imagePreview || v.image ? (
                              <img src={v.imagePreview || v.image} alt="Variant" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-500">
                                <Image className="w-5 h-5" />
                              </div>
                            )}
                            <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                              <Upload className="w-3 h-3 text-white" />
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { const nv = [...variants]; nv[i].imageFile = file; nv[i].imagePreview = reader.result; setVariants(nv); }; reader.readAsDataURL(file); }}} />
                            </label>
                          </div>
                          {/* Variant Fields */}
                          <div className="flex-1 grid grid-cols-4 gap-2">
                          <Input
                            placeholder="Name (e.g. Large, Red)"
                            value={v.name}
                            onChange={(e) => { const nv = [...variants]; nv[i].name = e.target.value; setVariants(nv); }}
                            className="bg-slate-900 border-slate-700 text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={v.price}
                            onChange={(e) => { const nv = [...variants]; nv[i].price = e.target.value; setVariants(nv); }}
                            className="bg-slate-900 border-slate-700 text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Stock"
                            value={v.stock}
                            onChange={(e) => { const nv = [...variants]; nv[i].stock = e.target.value; setVariants(nv); }}
                            className="bg-slate-900 border-slate-700 text-sm"
                          />
                          <Input
                            placeholder="SKU"
                            value={v.sku}
                            onChange={(e) => { const nv = [...variants]; nv[i].sku = e.target.value; setVariants(nv); }}
                            className="bg-slate-900 border-slate-700 text-sm"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setVariants(variants.filter((_, idx) => idx !== i))}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-slate-500">Each variant uses 1 token/day when token system activates</p>
                    </div>
                  )}
                </div>

                {/* SKU */}
                <div>
                  <Label className="text-slate-300">SKU (Stock Keeping Unit)</Label>
                  <Input
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder="Auto-generated if left blank"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="product-sku-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank to auto-generate (e.g. LT-HOM-123456)</p>
                </div>

                {/* Weight Input for Shipping Calculation */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-emerald-400" />
                    Weight (lbs) *
                    <span className="text-xs text-slate-500 font-normal ml-1">(required for shipping)</span>
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="200"
                      value={weightLbs}
                      onChange={(e) => setWeightLbs(e.target.value)}
                      placeholder="e.g. 2.5"
                      className="bg-slate-900 border-slate-700"
                      data-testid="product-weight-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Used to calculate accurate shipping at checkout. Leave blank to use default.
                  </p>
                </div>
                
                <div>
                  <Label className="text-slate-300">Tags</Label>
                  <div className="relative mt-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="tag1, tag2, tag3..."
                      className="bg-slate-900 border-slate-700 pl-10"
                      data-testid="product-tags-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Separate tags with commas</p>
                </div>
              </div>
            </Card>

            {/* Product Condition - Phase 1 */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#00A86B]" />
                Product Condition
                <span className="text-red-400">*</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PRODUCT_CONDITIONS.map((cond) => (
                  <button
                    key={cond.value}
                    type="button"
                    onClick={() => setCondition(cond.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      condition === cond.value
                        ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400'
                        : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                    data-testid={`condition-${cond.value}`}
                  >
                    <div className="font-medium text-sm">{cond.label}</div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Sale Mode & Auction Settings - Phase 2 */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-[#00A86B]" />
                Selling Type
              </h3>
              
              {/* Sale Mode Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {SALE_MODES.map((mode) => {
                  const ModeIcon = mode.icon;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => {
                        setSaleMode(mode.value);
                        if (mode.value === 'FIXED_PRICE') {
                          setBuyNowEnabled(false);
                        } else if (mode.value === 'AUCTION') {
                          setBuyNowEnabled(false);
                        } else if (mode.value === 'FIXED_OR_AUCTION') {
                          setBuyNowEnabled(true);
                        }
                      }}
                      className={`p-4 rounded-lg border flex items-center gap-3 transition-all ${
                        saleMode === mode.value
                          ? 'bg-emerald-600/20 border-emerald-500'
                          : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`sale-mode-${mode.value}`}
                    >
                      <ModeIcon className={`w-5 h-5 ${saleMode === mode.value ? 'text-emerald-400' : 'text-slate-400'}`} />
                      <span className={saleMode === mode.value ? 'text-emerald-400' : 'text-slate-300'}>{mode.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Auction Settings (shown when auction enabled) */}
              {(saleMode === 'AUCTION' || saleMode === 'FIXED_OR_AUCTION') && (
                <div className="space-y-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-2 text-amber-400 text-sm mb-4">
                    <AlertCircle className="w-4 h-4" />
                    <span>Auction Settings</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Start Date/Time */}
                    <div>
                      <Label className="text-slate-300 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        Auction Start
                      </Label>
                      <Input
                        type="datetime-local"
                        value={auctionStartAt}
                        onChange={(e) => setAuctionStartAt(e.target.value)}
                        className="mt-1 bg-slate-800 border-slate-700"
                        data-testid="auction-start-input"
                      />
                    </div>
                    
                    {/* End Date/Time */}
                    <div>
                      <Label className="text-slate-300 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-red-400" />
                        Auction End
                      </Label>
                      <Input
                        type="datetime-local"
                        value={auctionEndAt}
                        onChange={(e) => setAuctionEndAt(e.target.value)}
                        className="mt-1 bg-slate-800 border-slate-700"
                        data-testid="auction-end-input"
                      />
                    </div>
                    
                    {/* Minimum Bid */}
                    <div>
                      <Label className="text-slate-300">Minimum Starting Bid (JMD)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={auctionMinBid}
                        onChange={(e) => setAuctionMinBid(e.target.value)}
                        placeholder="e.g. 1000"
                        className="mt-1 bg-slate-800 border-slate-700"
                        data-testid="auction-min-bid-input"
                      />
                    </div>
                    
                    {/* Minimum Increment */}
                    <div>
                      <Label className="text-slate-300">Bid Increment (JMD)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={auctionMinIncrement}
                        onChange={(e) => setAuctionMinIncrement(e.target.value)}
                        placeholder="100"
                        className="mt-1 bg-slate-800 border-slate-700"
                        data-testid="auction-increment-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Minimum amount above current bid</p>
                    </div>
                  </div>
                  
                  {/* Buy Now Price (for FIXED_OR_AUCTION mode) */}
                  {saleMode === 'FIXED_OR_AUCTION' && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <Label className="text-slate-300 flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-emerald-400" />
                        Buy Now Price (JMD)
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={buyNowPrice}
                        onChange={(e) => setBuyNowPrice(e.target.value)}
                        placeholder="Instant purchase price"
                        className="mt-1 bg-slate-800 border-slate-700"
                        data-testid="buy-now-price-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Buyers can skip bidding and purchase immediately at this price</p>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Images */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-[#00A86B]" />
                Product Images
                <span className="text-sm font-normal text-slate-400">
                  ({images.length + imageFiles.length}/10)
                </span>
              </h3>
              
              {/* Image Grid */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-4">
                {/* Existing images */}
                {images.map((url, index) => (
                  <div key={`existing-${index}`} className="relative group">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 overflow-hidden">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index, true)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                    {index === 0 && (
                      <Badge className="absolute bottom-1 left-1 bg-[#00A86B] text-xs">Main</Badge>
                    )}
                  </div>
                ))}
                
                {/* New images */}
                {imageFiles.map((item, index) => (
                  <div key={`new-${index}`} className="relative group">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 overflow-hidden border-2 border-[#00A86B]">
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index, false)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                    <Badge className="absolute bottom-1 left-1 bg-blue-500 text-xs">New</Badge>
                  </div>
                ))}
                
                {/* Upload button */}
                {images.length + imageFiles.length < 10 && (
                  <label className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-700 flex flex-col items-center justify-center cursor-pointer hover:border-[#00A86B] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-8 h-8 text-slate-500 mb-2" />
                    <span className="text-sm text-slate-500">Add Image</span>
                  </label>
                )}
              </div>
              
              <p className="text-sm text-slate-400">
                First image will be the main product image. Max 5MB per image.
              </p>
            </Card>


            {/* Product Video */}
            {editingProduct ? (
              <ProductVideoUpload
                productId={editingProduct.id}
                videos={videos}
                onVideoChange={setVideos}
              />
            ) : (
              <Card className="border border-gray-200 bg-slate-800/50 border-slate-700">
                <div className="p-6">
                  <div className="flex items-center gap-2 text-base font-semibold mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polygon points="23 7 16 12 23 17 23 7"/><rect width="15" height="14" x="1" y="5" rx="2" ry="2"/></svg>
                    Product Video
                    <span className="text-xs font-normal text-slate-400 border border-slate-600 rounded px-2 py-0.5">Optional</span>
                  </div>
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 text-slate-500"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
                    <p className="text-sm font-medium text-slate-300 mb-1">Video Upload Available After Save</p>
                    <p className="text-xs text-slate-500">Save your product first, then you can add a video</p>
                  </div>
                </div>
              </Card>
            )}
            {/* Actions */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => setShowPreview(true)}
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                data-testid="preview-product-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploading ? 'Uploading images...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          /* Product List */
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="bg-slate-900 border-slate-700 pl-10"
              />
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <Card className="p-12 bg-slate-800/50 border-slate-700 text-center">
                <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Products Yet</h3>
                <p className="text-slate-400 mb-6">Start adding products to your store</p>
                <Button
                  onClick={() => policyAccepted ? setShowForm(true) : setShowPolicyModal(true)}
                  className="bg-[#00A86B] hover:bg-[#00A86B]/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Product
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className={`bg-slate-800/50 border-slate-700 overflow-hidden ${!product.is_active ? 'opacity-60' : ''}`}>
                    {/* Image */}
                    <div className="h-40 bg-slate-900 relative">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-600" />
                        </div>
                      )}
                      {!product.is_active && (
                        <Badge className="absolute top-2 left-2 bg-slate-700">Hidden</Badge>
                      )}
                      {product.is_featured && (
                        <Badge className="absolute top-2 right-2 bg-[#FFD300] text-black">Featured</Badge>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h3 className="font-semibold truncate">{product.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold text-[#00A86B]">J${product.price?.toLocaleString()}</span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-sm text-slate-500 line-through">J${product.original_price?.toLocaleString()}</span>
                        )}
                      </div>
                      {product.stock_quantity <= 0 && (
                        <Badge className="mt-1 bg-red-500/20 text-red-400 border-red-500/30">Out of Stock</Badge>
                      )}
                      {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
                        <Badge className="mt-1 bg-amber-500/20 text-amber-400 border-amber-500/30">Low Stock: {product.stock_quantity}</Badge>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {product.category && (
                          <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">{product.category}</Badge>
                        )}
                        {product.sku && (
                          <Badge variant="outline" className="border-slate-600 text-slate-500 text-xs">SKU: {product.sku}</Badge>
                        )}
                        {product.free_shipping && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Free Ship</Badge>
                        )}
                      </div>
                      <div className="flex gap-3 mt-2 text-xs text-slate-500">
                        <span data-testid="product-stock">{product.stock_quantity || 0} in stock</span>
                        <span>{product.views_count || 0} views</span>
                        <span>{product.sold_this_month || product.sales_count || 0} sold</span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(product)}
                          className="flex-1 border-slate-700"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid="delete-product-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(product)}
                          className={product.is_active ? 'text-slate-400' : 'text-[#00A86B]'}
                        >
                          {product.is_active ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Seller Terms Agreement Dialog */}

      {/* Product Preview Modal */}
      <AlertDialog open={showPreview} onOpenChange={setShowPreview}>
        <AlertDialogContent className="max-w-lg max-h-[85vh] overflow-auto bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              Product Preview
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4" data-testid="product-preview-modal">
            {/* Image preview */}
            <div className="aspect-square bg-slate-800 rounded-lg overflow-hidden flex items-center justify-center">
              {images.length > 0 ? (
                <img src={typeof images[0] === 'string' ? images[0] : URL.createObjectURL(images[0])} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-slate-500 text-center">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No image uploaded</p>
                </div>
              )}
            </div>
            {/* Product info */}
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-white">{title || 'Product Title'}</h3>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-emerald-400">J${parseFloat(price || 0).toLocaleString()}</span>
                {originalPrice && parseFloat(originalPrice) > parseFloat(price) && (
                  <span className="text-lg text-slate-500 line-through">J${parseFloat(originalPrice).toLocaleString()}</span>
                )}
              </div>
              {freeShipping && (
                <span className="inline-block px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Free Shipping</span>
              )}
              <div className="flex gap-2 flex-wrap">
                {category && <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{category}</span>}
                {condition && <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{condition.replace(/_/g, ' ')}</span>}
                {stockQuantity && <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{stockQuantity} in stock</span>}
                {weightLbs && <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{weightLbs} lbs</span>}
              </div>
              {description && (
                <p className="text-slate-400 text-sm mt-2 whitespace-pre-wrap">{description.slice(0, 200)}{description.length > 200 ? '...' : ''}</p>
              )}
              {tags && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {tags.split(',').filter(Boolean).map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">#{tag.trim()}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-auto bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Seller Agreement & Prohibited Items
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-slate-300">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-amber-200 text-sm">
                    By listing products on Liantiao, you agree to comply with our seller terms and prohibited items policy.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white">Prohibited Items Include:</h4>
                  <ul className="text-sm space-y-1 text-slate-400">
                    <li>• Cash, monetary instruments, gift cards</li>
                    <li>• Weapons, firearms, ammunition, explosives</li>
                    <li>• Illegal drugs, controlled substances</li>
                    <li>• Hazardous materials, flammables</li>
                    <li>• Stolen goods, counterfeit products</li>
                    <li>• Items restricted by Jamaican law</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white">Seller Responsibilities:</h4>
                  <ul className="text-sm space-y-1 text-slate-400">
                    <li>• Accurate product descriptions and images</li>
                    <li>• Timely order fulfillment</li>
                    <li>• Proper packaging for safe delivery</li>
                    <li>• Compliance with all applicable laws</li>
                    <li>• Maintain product quality standards</li>
                  </ul>
                </div>
                
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-xs text-slate-400">
                    Violation of these terms may result in listing removal, account suspension, and legal action.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-slate-800 text-white border-slate-700 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              I Agree - Save Product
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
